import { ChangeEvent, ReactNode, useCallback, useState } from 'react';
import { GraphQLTaggedNode } from 'react-relay';

import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

import { debounce, useAvoidSuspense } from 'lib';
import { OperationType } from 'relay-runtime';

type BaseProps<O extends {} = {}> = AutocompleteProps<O, boolean, false, false>;

type Props<O extends {} = {}, Q extends OperationType = OperationType> = Omit<
  BaseProps<O>,
  'renderInput' | 'options'
> & {
  autoFocus?: boolean;
  label: ReactNode;
  fragment: GraphQLTaggedNode;
  data: any;
  getOptions: (data: any) => readonly O[];
  query: GraphQLTaggedNode;
  error?: boolean;
  helperText?: ReactNode;
  getVariables?: (search: string) => Q['variables'];
};

export type AsyncSelectProps<O extends {} = {}> = Props<O>;

function AsyncSelect<
  O extends {} = {},
  Q extends OperationType = OperationType
>(props: Props<O, Q>) {
  const {
    label,
    autoFocus,
    fragment,
    query,
    error,
    helperText,
    getOptions,
    getVariables,
    ...rest
  } = props;
  const [search, setSearch] = useState('');
  const [data, { refetch, loading }] = useAvoidSuspense<Q>(
    fragment,
    props.data,
    query
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchString = event.currentTarget.value;
      setSearch(searchString);

      debounce(() => {
        refetch(
          getVariables ? getVariables(searchString) : { search: searchString }
        );
      }, 500);
    },
    [getVariables, refetch]
  );

  return (
    <Autocomplete
      options={getOptions(data)}
      loading={loading}
      filterOptions={x => x}
      noOptionsText="Ieškoti..."
      loadingText="Ieškoma..."
      renderInput={params => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={label}
          variant="standard"
          value={search}
          onChange={handleChange}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...rest}
    />
  );
}

export default AsyncSelect;
